// Ressentis (D-143, maquettes r1 à r3) : logique pure, testée (ressentis.test.ts). Les écrans sont dans src/ui/ressentis.ts.
//  * Une seule échelle, 5 visages, pour le match, la séance et le bilan de saison : 1 Dur, 2 Bof, 3 Moyen, 4 Bien, 5 Top.
//  * Des mots rapides pour aider les plus jeunes à écrire, accordés au genre de l'équipe (agree, D-008 : mixte au masculin).
//  * La fenêtre : jusqu'au lendemain midi (heure du club) ; le coach : « À regarder » (une note basse, une baisse, le silence).
import { agree, type TeamGender } from './labels';

export type Score = 1 | 2 | 3 | 4 | 5;
export const SCORES: Score[] = [1, 2, 3, 4, 5];
export const FACE_LABEL: Record<Score, string> = { 1: 'Dur', 2: 'Bof', 3: 'Moyen', 4: 'Bien', 5: 'Top' };
/** Les couleurs des visages : rouge, orange, ocre, vert clair, vert (lisibles en clair et en sombre). */
export const FACE_COLOR: Record<Score, string> = { 1: '#D9412B', 2: '#E9772A', 3: '#D19A12', 4: '#5FA83A', 5: '#1E9E5A' };

/** La note la plus proche d'une moyenne (3,7 → 4, « Bien »). */
export const roundScore = (x: number): Score => Math.max(1, Math.min(5, Math.round(x))) as Score;
/** « 3,7 » ; rien : « — ». */
export const fmtAvg = (x: number | null | undefined): string => (x == null || Number.isNaN(x) ? '—' : x.toFixed(1).replace('.', ','));

/** Les mots rapides, accordés : « Fatiguée », « Fière de l’équipe » chez les filles. */
export const quickWords = (g: TeamGender): string[] =>
  [agree(g, 'Fatigué', 'Fatiguée'), 'Temps de jeu', 'Petit bobo', 'Super ambiance', agree(g, 'Fier de l’équipe', 'Fière de l’équipe')];

export const average = (scores: readonly number[]): number | null => (scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null);
/** La répartition des 5 visages : [Dur, Bof, Moyen, Bien, Top]. */
export const distribution = (scores: readonly number[]): number[] => SCORES.map(s => scores.filter(x => x === s).length);

/** « jusqu’à demain midi » / « jusqu’à midi » / « fermé » ; `closes` : le lendemain midi de l'occurrence. */
export function closesLabel(closesIso: string, now = new Date()): string {
  const c = new Date(closesIso);
  if (now >= c) return 'fermé';
  const day = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  return day(c) === day(now) ? 'jusqu’à midi' : 'jusqu’à demain midi';
}

/** Les 5 derniers ressentis d'une personne : une note, 0 (pas répondu), -1 (absente). */
export type Strip = number[];
export interface Watch { kind: 'down' | 'silent' | 'low'; text: string }
/** « À regarder » (maquette r2c) : en baisse depuis 3 séances (3 notes qui descendent, la dernière à 2 ou moins), plus de
 *  réponse depuis 3 séances (sans les absences), ou la dernière note à 2 ou moins. */
export function watchOf(strip: Strip): Watch | null {
  const answered = strip.filter(v => v > 0);
  const last3 = answered.slice(-3);
  const lastSeen = strip.filter(v => v !== -1).slice(-3);
  if (lastSeen.length === 3 && lastSeen.every(v => v === 0) && strip.some(v => v > 0)) return { kind: 'silent', text: 'Plus de réponse depuis 3 séances' };
  if (last3.length === 3 && last3[0]! > last3[1]! && last3[1]! > last3[2]! && last3[2]! <= 2) return { kind: 'down', text: 'En baisse depuis 3 séances' };
  const last = strip.filter(v => v !== -1 && v !== 0).at(-1);
  if (last != null && last <= 2 && strip.at(-1) === last) return { kind: 'low', text: 'Dernier ressenti bas' };
  return null;
}

/** Le libellé d'une semaine dans les colonnes : « 7 sept » au premier lundi et à chaque nouveau mois, sinon le jour. */
export function weekLabels(mondays: readonly string[]): string[] {
  let month = -1;
  return mondays.map(iso => {
    const d = new Date(`${iso}T12:00:00`);
    const m = d.getMonth();
    const label = m !== month ? `${d.getDate()} ${d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '')}` : String(d.getDate());
    month = m;
    return label;
  });
}

/** La tendance d'une équipe (bureau) : la phrase sous son nom. */
export function trendText(t: { trend: 'down' | 'up' | 'stable' | null; few: boolean }, weeksDown = 2): string {
  if (t.few) return '';
  if (t.trend === 'down') return `En baisse depuis ${weeksDown} semaines`;
  if (t.trend === 'up') return 'En hausse';
  return '';
}

/** Le taux de réponse en pourcentage (« 82 % »), ou null. */
export const ratePct = (n: number, expected: number): number | null => (expected > 0 ? Math.round((Math.min(n, expected) / expected) * 100) : null);
